import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { MagnifyingGlass, User, Package } from "@phosphor-icons/react";

export default function HeroSearch() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState({ artists: [], products: [] });
  const nav = useNavigate();
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const trimmed = q.trim();
    if (!trimmed) {
      setSuggestions({ artists: [], products: [] });
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search/suggest", { params: { q: trimmed, limit: 5 } });
        setSuggestions(data);
      } catch {}
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const submit = (e) => {
    e.preventDefault();
    const term = q.trim();
    if (!term) return;
    nav(`/catalogue?q=${encodeURIComponent(term)}`);
    setOpen(false);
  };

  const hasResults = suggestions.artists.length + suggestions.products.length > 0;

  return (
    <div ref={containerRef} className="relative w-full max-w-2xl" data-testid="hero-search">
      <form onSubmit={submit}>
        <div className={`flex items-center gap-3 bg-black/70 backdrop-blur-md border-2 ${open && hasResults ? "border-[#FF3B00]" : "border-white/30"} px-5 py-4 transition-colors`}>
          <MagnifyingGlass size={22} weight="bold" className="text-[#FF3B00] flex-shrink-0" />
          <input
            data-testid="hero-search-input"
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Cherchez un pilote, un hoodie, une casquette..."
            className="flex-1 bg-transparent outline-none text-base md:text-lg placeholder:text-white/40"
          />
          <button type="submit" className="hidden md:block px-4 py-1.5 bg-[#FF3B00] text-white uppercase tracking-widest text-xs hover:bg-[#E03500]">
            Chercher
          </button>
        </div>
      </form>

      {open && q.trim() && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-black border-2 border-[#FF3B00] max-h-[70vh] overflow-y-auto z-50" data-testid="hero-search-results">
          {!hasResults ? (
            <div className="p-6 text-center text-white/50 text-sm">
              Aucun résultat pour <span className="text-white">« {q} »</span>
            </div>
          ) : (
            <>
              {suggestions.artists.length > 0 && (
                <div>
                  <div className="px-5 py-3 text-[10px] uppercase tracking-[0.3em] text-[#FF3B00] border-b border-white/10 bg-[#0A0A0A] flex items-center gap-2">
                    <User size={12} weight="bold" /> Pilotes
                  </div>
                  {suggestions.artists.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => { nav(`/pilotes/${a.id}`); setOpen(false); }}
                      data-testid={`suggest-artist-${a.id}`}
                      className="w-full flex items-center gap-4 px-5 py-3 hover:bg-[#FF3B00]/10 border-b border-white/5 text-left transition-colors">
                      <div className="w-10 h-10 bg-[#111] flex items-center justify-center font-display text-xl">
                        {(a.artist_name || "?")[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{a.artist_name}</div>
                        <div className="text-xs text-white/40 uppercase tracking-widest">Voir la boutique →</div>
                      </div>
                      {a.featured && (
                        <div className="text-[9px] uppercase tracking-widest bg-[#FF3B00] text-white px-2 py-0.5">Vedette</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
              {suggestions.products.length > 0 && (
                <div>
                  <div className="px-5 py-3 text-[10px] uppercase tracking-[0.3em] text-[#FF3B00] border-b border-white/10 bg-[#0A0A0A] flex items-center gap-2">
                    <Package size={12} weight="bold" /> Produits
                  </div>
                  {suggestions.products.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => { nav(`/produits/${p.id}`); setOpen(false); }}
                      data-testid={`suggest-product-${p.id}`}
                      className="w-full flex items-center gap-4 px-5 py-3 hover:bg-[#FF3B00]/10 border-b border-white/5 text-left transition-colors">
                      <div className="w-10 h-10 bg-[#111] overflow-hidden flex-shrink-0">
                        {p.image ? (
                          <img src={fileUrl(p.image)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white/30 text-xs">?</div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-white/40 uppercase tracking-widest">{p.artist_name}</div>
                        <div className="font-medium truncate">{p.name}</div>
                      </div>
                      <div className="font-display text-xl">${p.price?.toFixed(2)}</div>
                    </button>
                  ))}
                </div>
              )}
              <button
                onClick={submit}
                className="w-full px-5 py-3 bg-[#FF3B00]/10 hover:bg-[#FF3B00]/20 text-[#FF3B00] uppercase tracking-widest text-xs text-center border-t border-white/10">
                Voir tous les résultats pour « {q} » →
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
